import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { ArrowLeft, Download, AlertCircle, Loader2 } from 'lucide-react';
import type { Artefact } from '../../redux/api/portfolioApi';
import { useGetArtefactViewUrlMutation } from '../../redux/api/portfolioApi';
import { getCachedPDF, cachePDF } from '../../services/pdfCacheService';
import { useTheme } from '../../contexts';
import { highlightExcerptOnPage, clearHighlights } from '../../utils/pdfHighlight';
import { useAppSelector, useAppDispatch } from '../../redux/hooks';
import { eventsApi } from '../../redux/api/eventsApi';
import ContractTermsPanel from './ContractTermsPanel';
import EventExtractionPanel from './EventExtractionPanel';

// PDF Viewer imports
import { Viewer, Worker, SpecialZoomLevel, type SetRenderRange } from '@react-pdf-viewer/core';
import { defaultLayoutPlugin } from '@react-pdf-viewer/default-layout';
import { pageNavigationPlugin } from '@react-pdf-viewer/page-navigation';
import '@react-pdf-viewer/core/lib/styles/index.css';
import '@react-pdf-viewer/default-layout/lib/styles/index.css';
import '@react-pdf-viewer/page-navigation/lib/styles/index.css';

interface DocumentDetailViewProps {
  artefact: Artefact;
  siteId: string;
  onBack?: () => void;
}

type DetailTab = 'events' | 'contract' | 'qa';

// Render all page containers so jumpToPage works correctly
const renderAllPages: SetRenderRange = (visiblePagesRange) => ({
  startPage: 0,
  endPage: visiblePagesRange.numPages - 1,
});

/**
 * Determine which tabs to show and the default tab based on doc_type.
 */
function getTabsForDocType(docType: string | undefined): { tabs: { id: DetailTab; label: string }[]; defaultTab: DetailTab } {
  const upperType = (docType || '').toUpperCase();

  if (upperType === 'CONTRACT') {
    return {
      tabs: [
        { id: 'contract', label: 'Contract Terms' },
        { id: 'events', label: 'Events' },
        { id: 'qa', label: 'QA' },
      ],
      defaultTab: 'contract',
    };
  }

  if (upperType === 'ASBUILT' || upperType === 'DRAWING') {
    return {
      tabs: [
        { id: 'events', label: 'Events' },
        { id: 'qa', label: 'QA' },
      ],
      defaultTab: 'events',
    };
  }

  // All other doc types — show everything, events first
  return {
    tabs: [
      { id: 'events', label: 'Events' },
      { id: 'contract', label: 'Contract Terms' },
      { id: 'qa', label: 'QA' },
    ],
    defaultTab: 'events',
  };
}

const DocumentDetailView: React.FC<DocumentDetailViewProps> = ({ artefact, siteId, onBack }) => {
  const { resolvedTheme } = useTheme();
  const dispatch = useAppDispatch();
  const tenantId = useAppSelector((state) => state.auth.profile?.active_tenant?.id);

  // Doc-type aware tabs
  const { tabs, defaultTab } = useMemo(() => getTabsForDocType(artefact.doc_type), [artefact.doc_type]);
  const [activeTab, setActiveTab] = useState<DetailTab>(defaultTab);

  // Reset active tab when artefact changes
  useEffect(() => {
    setActiveTab(defaultTab);
  }, [defaultTab]);

  // --- PDF Viewer State ---
  const [pdfViewUrl, setPdfViewUrl] = useState<string | null>(null);
  const [viewerError, setViewerError] = useState<string | null>(null);
  const [isLoadingPdf, setIsLoadingPdf] = useState(false);
  const blobUrlRef = useRef<string | null>(null);
  const jumpToPageRef = useRef<((pageIndex: number) => void) | null>(null);

  const [getViewUrl] = useGetArtefactViewUrlMutation();

  // Plugin instances — created once via refs, memoize the array
  const pageNavigationPluginRef = useRef(pageNavigationPlugin());
  const pageNavigationPluginInstance = pageNavigationPluginRef.current;
  const { jumpToPage } = pageNavigationPluginInstance;

  const defaultLayoutPluginRef = useRef(defaultLayoutPlugin({
    sidebarTabs: () => [],
    toolbarPlugin: {
      fullScreenPlugin: {
        onEnterFullScreen: () => {},
        onExitFullScreen: () => {},
      },
      openFileButton: () => null,
    },
  }));
  const defaultLayoutPluginInstance = defaultLayoutPluginRef.current;

  const plugins = useMemo(
    () => [defaultLayoutPluginInstance, pageNavigationPluginInstance],
    [defaultLayoutPluginInstance, pageNavigationPluginInstance]
  );

  // Keep jumpToPage ref up to date
  useEffect(() => {
    jumpToPageRef.current = jumpToPage;
  }, [jumpToPage]);

  // --- Fetch & Cache PDF ---
  useEffect(() => {
    if (!artefact?.id || !tenantId) {
      setPdfViewUrl(null);
      return;
    }

    let isActive = true;

    // Clean up previous blob URL
    if (blobUrlRef.current) {
      URL.revokeObjectURL(blobUrlRef.current);
      blobUrlRef.current = null;
    }
    setPdfViewUrl(null);
    setViewerError(null);
    setIsLoadingPdf(true);

    const fetchPdf = async () => {
      try {
        // 1. Check IndexedDB cache first
        const cachedBlob = await getCachedPDF(artefact.id);
        if (cachedBlob && isActive) {
          const blobUrl = URL.createObjectURL(cachedBlob);
          blobUrlRef.current = blobUrl;
          setPdfViewUrl(blobUrl);
          setIsLoadingPdf(false);
          return;
        }

        // 2. Fetch signed URL from API
        const result = await getViewUrl({
          document_id: artefact.id,
          tenant_id: tenantId,
          expires_in: 3600,
        }).unwrap();

        if (!isActive) return;

        // 3. Download blob and cache
        try {
          const response = await fetch(result.url, {
            method: 'GET',
            mode: 'cors',
            credentials: 'omit',
            headers: { 'Accept': 'application/pdf' },
          });

          if (!response.ok) {
            throw new Error(`Failed to fetch PDF: ${response.statusText}`);
          }

          const blob = await response.blob();
          if (!isActive) return;

          // Cache in background (fire-and-forget)
          cachePDF(artefact.id, blob, siteId, tenantId).catch(() => {});

          if (blobUrlRef.current) {
            URL.revokeObjectURL(blobUrlRef.current);
          }
          const blobUrl = URL.createObjectURL(blob);
          blobUrlRef.current = blobUrl;
          setPdfViewUrl(blobUrl);
        } catch {
          if (!isActive) return;
          // Fallback to direct URL on fetch error (e.g. CORS)
          setPdfViewUrl(result.url);
        }
      } catch {
        if (!isActive) return;
        setViewerError('Failed to get document URL');
      } finally {
        if (isActive) {
          setIsLoadingPdf(false);
        }
      }
    };

    fetchPdf();

    return () => {
      isActive = false;
      if (blobUrlRef.current) {
        URL.revokeObjectURL(blobUrlRef.current);
        blobUrlRef.current = null;
      }
    };
  }, [artefact.id, tenantId, siteId, getViewUrl]);

  // --- Navigation helpers ---
  const goToPage = useCallback((page: number) => {
    if (page < 1) page = 1;
    const pageIndex = page - 1;
    if (jumpToPageRef.current) {
      jumpToPageRef.current(pageIndex);
    }
  }, []);

  // Listen for navigateToAsBuiltPage custom events (text highlighting)
  useEffect(() => {
    const handleNavigate = (event: Event) => {
      const detail = (event as CustomEvent).detail;
      if (!detail) return;

      const page = detail.page as number | undefined;
      const excerpt = detail.excerpt as string | undefined;

      if (page != null) {
        goToPage(page);

        // Highlight excerpt after a short delay to let the page render
        if (excerpt) {
          clearHighlights();
          setTimeout(() => {
            const pageContainers = document.querySelectorAll('[data-testid^="core__page-layer"]');
            const target = pageContainers[page - 1];
            if (target) {
              highlightExcerptOnPage(target, excerpt, true, 'rgba(250, 204, 21, 0.7)');
            }
          }, 500);
        }
      }
    };

    window.addEventListener('navigateToAsBuiltPage', handleNavigate);
    return () => {
      window.removeEventListener('navigateToAsBuiltPage', handleNavigate);
      clearHighlights();
    };
  }, [goToPage]);

  // --- Tab click handlers ---
  const handlePageClick = useCallback((page: number) => {
    goToPage(page);
  }, [goToPage]);

  const handleEventClick = useCallback(async (eventId: string) => {
    // Fetch event detail to get source page/excerpt for highlighting
    try {
      const detail = await dispatch(
        eventsApi.endpoints.getSiteEvent.initiate({ siteId, eventId })
      ).unwrap();

      const page = detail?.meta?.source_page as number | undefined;
      const excerpt = detail?.meta?.source_excerpt as string | undefined;

      if (page) {
        goToPage(page);
        // Highlight in bright yellow after page renders
        clearHighlights();
        setTimeout(() => {
          const pageContainers = document.querySelectorAll('[data-testid^="core__page-layer"]');
          const target = pageContainers[page - 1];
          if (target && excerpt) {
            highlightExcerptOnPage(target, excerpt, true, 'rgba(250, 204, 21, 0.7)');
          }
        }, 500);
      }
    } catch {
      // If fetch fails, fall back to opening events tab
      window.dispatchEvent(
        new CustomEvent('openTab', { detail: { tabType: 'eventstasks' } })
      );
    }
  }, [siteId, dispatch, goToPage]);

  const handleDownload = () => {
    window.dispatchEvent(
      new CustomEvent('openTab', {
        detail: { tabType: 'documents' },
      })
    );
  };

  return (
    <div className="flex flex-col h-full" style={{ backgroundColor: 'var(--color-bg-primary)' }}>
      {/* Header */}
      <div
        className="flex items-center gap-3 px-4 py-3 border-b"
        style={{ borderColor: 'var(--color-border)' }}
      >
        {onBack && (
          <button
            onClick={onBack}
            className="p-1 rounded-md transition-colors"
            style={{ color: 'var(--color-text-secondary)' }}
            onMouseEnter={(e) => {
              (e.currentTarget as HTMLElement).style.backgroundColor = 'var(--color-surface-hover)';
            }}
            onMouseLeave={(e) => {
              (e.currentTarget as HTMLElement).style.backgroundColor = 'transparent';
            }}
          >
            <ArrowLeft className="w-4 h-4" />
          </button>
        )}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <h2 className="text-sm font-semibold eg-text-primary truncate">
              {artefact.title || artefact.filename}
            </h2>
            {artefact.doc_type && (
              <span
                className="text-[10px] font-medium px-1.5 py-0.5 rounded flex-shrink-0"
                style={{
                  backgroundColor: 'rgba(23, 196, 128, 0.15)',
                  color: '#17c480',
                }}
              >
                {artefact.doc_type}
              </span>
            )}
          </div>
          {/* Revision breadcrumb (mock) */}
          <div className="flex items-center gap-1 mt-0.5">
            <span className="text-[10px] eg-text-muted">v1 (2024-01)</span>
            <span className="text-[10px] eg-text-muted">&middot;</span>
            <span className="text-[10px] eg-text-muted">v2 (2024-06)</span>
            <span className="text-[10px] eg-text-muted">&middot;</span>
            <span className="text-[10px] font-semibold text-engramiq-green">v3 &#9733;</span>
          </div>
        </div>
        <button
          onClick={handleDownload}
          className="eg-btn-secondary flex items-center gap-1.5 text-xs px-3 py-1.5"
        >
          <Download className="w-3.5 h-3.5" />
          Download
        </button>
      </div>

      {/* Split Layout */}
      <div className="flex-1 flex min-h-0">
        {/* Left: PDF Viewer */}
        <div
          className="flex-1 relative overflow-hidden"
          style={{ backgroundColor: 'var(--color-bg-tertiary)' }}
        >
          {viewerError ? (
            <div className="absolute inset-0 flex items-center justify-center" style={{ backgroundColor: 'var(--color-surface)' }}>
              <div className="text-center p-8">
                <AlertCircle className="w-8 h-8 mx-auto mb-2" style={{ color: 'var(--color-text-muted)' }} />
                <p className="text-sm eg-text-primary mb-1">Cannot Display Document</p>
                <p className="text-xs eg-text-muted">{viewerError}</p>
              </div>
            </div>
          ) : isLoadingPdf ? (
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="text-center">
                <Loader2 className="w-8 h-8 text-engramiq-green animate-spin mx-auto mb-3" />
                <p className="text-sm eg-text-secondary">Loading document...</p>
              </div>
            </div>
          ) : pdfViewUrl ? (
            <Worker workerUrl="https://unpkg.com/pdfjs-dist@3.11.174/build/pdf.worker.min.js">
              <div className={`h-full pdf-viewer-${resolvedTheme}
                [&_.rpv-core__viewer]:h-full
                [&_.rpv-core__inner-container]:!bg-[var(--color-surface)]
                [&_.rpv-default-layout__container]:!bg-[var(--color-surface)]
                [&_.rpv-default-layout__toolbar]:!bg-[var(--color-bg-primary)]
                [&_.rpv-default-layout__toolbar]:!border-b
                [&_.rpv-default-layout__toolbar]:!border-[var(--color-border)]
                [&_.rpv-core__minimal-button]:!text-[var(--color-text-primary)]
                [&_.rpv-core__minimal-button:hover]:!bg-[var(--color-surface-hover)]
                [&_.rpv-core__textbox]:!bg-[var(--color-surface)]
                [&_.rpv-core__textbox]:!text-[var(--color-text-primary)]
                [&_.rpv-core__textbox]:!border-[var(--color-border)]
                [&_.rpv-zoom__popover-target-scale]:!text-[var(--color-text-primary)]
                [&_.rpv-page-navigation__current-page-input]:!bg-[var(--color-surface)]
                [&_.rpv-page-navigation__current-page-input]:!text-[var(--color-text-primary)]
                [&_.rpv-open__button]:!hidden
                [&_button[aria-label*='Open']]:!hidden
                [&_button[title*='Open']]:!hidden
              `}>
                <Viewer
                  fileUrl={pdfViewUrl}
                  plugins={plugins}
                  defaultScale={SpecialZoomLevel.PageWidth}
                  theme={resolvedTheme}
                  setRenderRange={renderAllPages}
                  renderError={() => (
                    <div className="flex items-center justify-center h-full">
                      <div className="text-center">
                        <AlertCircle className="w-8 h-8 mx-auto mb-2" style={{ color: 'var(--color-text-muted)' }} />
                        <p className="text-sm eg-text-primary">Failed to load PDF</p>
                      </div>
                    </div>
                  )}
                />
              </div>
            </Worker>
          ) : (
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="text-center p-8">
                <AlertCircle className="w-8 h-8 mx-auto mb-2" style={{ color: 'var(--color-text-muted)' }} />
                <p className="text-sm eg-text-secondary">No PDF available</p>
              </div>
            </div>
          )}
        </div>

        {/* Right: Tabbed Panel */}
        <div
          className="w-[380px] flex flex-col min-h-0 border-l"
          style={{ backgroundColor: 'var(--color-bg-primary)', borderColor: 'var(--color-border)' }}
        >
          {/* Tab Switcher */}
          <div
            className="flex border-b"
            style={{ borderColor: 'var(--color-border)' }}
          >
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className="flex-1 px-3 py-2.5 text-xs font-medium transition-colors relative"
                style={{
                  color: activeTab === tab.id ? '#17c480' : 'var(--color-text-secondary)',
                }}
              >
                {tab.label}
                {activeTab === tab.id && (
                  <span
                    className="absolute bottom-0 left-0 right-0 h-[2px]"
                    style={{ backgroundColor: '#17c480' }}
                  />
                )}
              </button>
            ))}
          </div>

          {/* Tab Content */}
          <div className="flex-1 overflow-y-auto">
            {activeTab === 'events' && (
              <EventExtractionPanel
                documentId={artefact.id}
                siteId={siteId}
                onEventClick={handleEventClick}
              />
            )}
            {activeTab === 'contract' && (
              <ContractTermsPanel
                documentId={artefact.id}
                onPageClick={handlePageClick}
              />
            )}
            {activeTab === 'qa' && (
              <div className="flex items-center justify-center h-full p-8">
                <div className="text-center space-y-2">
                  <AlertCircle className="w-8 h-8 mx-auto" style={{ color: 'var(--color-text-muted)' }} />
                  <p className="text-sm eg-text-secondary">QA data not available</p>
                  <p className="text-xs eg-text-muted">
                    Run QA analysis on this document to see results here.
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DocumentDetailView;
